import React, { useEffect, useRef } from "react";

const CALENDLY_SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";
const CALENDLY_CSS_HREF = "https://assets.calendly.com/assets/external/widget.css";

/**
 * Inline Calendly booking widget.
 * Loads the widget.js script once on mount and renders the embedded calendar.
 */
export default function CalendlyEmbed({ url, prefill = {}, height = 720 }) {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!url) return undefined;

        // Inject CSS once
        if (!document.querySelector(`link[href="${CALENDLY_CSS_HREF}"]`)) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = CALENDLY_CSS_HREF;
            document.head.appendChild(link);
        }

        const initWidget = () => {
            if (!containerRef.current || !window.Calendly) return;
            // Clear any previous mount (e.g. on prefill change)
            containerRef.current.innerHTML = "";
            // Append prefill values directly to the URL query string so they
            // appear in the iframe `src` immediately (Calendly also sends them
            // via postMessage once the iframe loads — this is belt-and-braces
            // for slow networks).
            let finalUrl = url;
            try {
                const u = new URL(url);
                if (prefill.name) u.searchParams.set("name", prefill.name);
                if (prefill.email) u.searchParams.set("email", prefill.email);
                if (prefill.customAnswers?.a1)
                    u.searchParams.set("a1", prefill.customAnswers.a1);
                finalUrl = u.toString();
            } catch {
                /* fall through with raw url */
            }
            window.Calendly.initInlineWidget({
                url: finalUrl,
                parentElement: containerRef.current,
                prefill,
            });
        };

        const existing = document.querySelector(`script[src="${CALENDLY_SCRIPT_SRC}"]`);
        if (existing && window.Calendly) {
            initWidget();
        } else if (existing) {
            existing.addEventListener("load", initWidget, { once: true });
        } else {
            const script = document.createElement("script");
            script.src = CALENDLY_SCRIPT_SRC;
            script.async = true;
            script.addEventListener("load", initWidget, { once: true });
            document.body.appendChild(script);
        }

        return () => {
            if (containerRef.current) containerRef.current.innerHTML = "";
        };
    }, [url, prefill]);

    if (!url) return null;
    return (
        <div
            ref={containerRef}
            data-testid="calendly-embed"
            style={{
                minWidth: "320px",
                width: "100%",
                height: `${height}px`,
                colorScheme: "light",
            }}
        />
    );
}
